import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Check } from 'lucide-react';
import html2canvas from 'html2canvas';

interface RoofPitchAngleMeasurerProps {
  onAngleCaptured: (angle: string, screenshotDataUrl?: string) => void;
  onClose: () => void;
}

export const RoofPitchAngleMeasurer: React.FC<RoofPitchAngleMeasurerProps> = ({
  onAngleCaptured,
  onClose
}) => {
  const [currentAngle, setCurrentAngle] = useState<number>(0);
  const [isListening, setIsListening] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const angleHistoryRef = useRef<number[]>([]);
  const lastValidAngleRef = useRef<number>(0);
  const screenshotRef = useRef<HTMLDivElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // Smoothing buffer size - increased for more stability
  const BUFFER_SIZE = 15;

  // Handle orientation event - Define first
  const handleOrientation = useCallback((event: DeviceOrientationEvent) => {
    if (event.beta !== null) {
      // Beta is the tilt from front to back (-180 to 180)
      // For roof pitch, we want the angle from horizontal
      let rawAngle = event.beta;
      
      // Normalize to 0-90 range (roof pitch)
      // If beta is negative, take absolute value
      // If beta > 90, it means phone is upside down, adjust
      if (rawAngle < 0) {
        rawAngle = Math.abs(rawAngle);
      }
      if (rawAngle > 90) {
        rawAngle = 180 - rawAngle;
      }
      
      // Clamp between 0 and 90 degrees (valid roof pitch range)
      const clampedAngle = Math.min(90, Math.max(0, rawAngle));
      
      // Detect sudden jumps (>30 degrees) and reject them
      if (angleHistoryRef.current.length > 0) {
        const recentAverage = angleHistoryRef.current.slice(-5).reduce((a, b) => a + b, 0) / Math.min(5, angleHistoryRef.current.length);
        const diff = Math.abs(clampedAngle - recentAverage);
        
        // Reject sudden jumps that are too large
        if (diff > 30) {
          console.log(`[Angle Measurer] Rejecting sudden jump: ${recentAverage}° -> ${clampedAngle}° (diff: ${diff}°)`);
          return; // Don't add this reading
        }
      }
      
      // Add to smoothing buffer
      angleHistoryRef.current.push(clampedAngle);
      if (angleHistoryRef.current.length > BUFFER_SIZE) {
        angleHistoryRef.current.shift();
      }

      // Calculate smoothed angle (moving average)
      const sum = angleHistoryRef.current.reduce((acc, val) => acc + val, 0);
      const smoothed = sum / angleHistoryRef.current.length;
      
      lastValidAngleRef.current = smoothed;
      setCurrentAngle(smoothed);
    }
  }, []);

  // Request permission and start listening
  const requestPermissionAndStart = useCallback(async () => {
    try {
      console.log('[Angle Measurer] Requesting device orientation permission...');
      
      // Check if DeviceOrientationEvent exists
      if (typeof DeviceOrientationEvent === 'undefined') {
        throw new Error('Device orientation not supported on this device');
      }

      // For iOS 13+, we need explicit permission
      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        const permission = await (DeviceOrientationEvent as any).requestPermission();
        console.log('[Angle Measurer] Permission response:', permission);
        
        if (permission !== 'granted') {
          throw new Error('Permission denied for device orientation');
        }
      }

      setPermissionGranted(true);
      setIsListening(true);
      console.log('[Angle Measurer] Permission granted, starting listener');

      // Add orientation listener
      window.addEventListener('deviceorientation', handleOrientation);

    } catch (err: any) {
      console.error('[Angle Measurer] Permission error:', err);
      setError(err.message || 'Failed to access device motion sensors');
      setIsListening(false);
    }
  }, [handleOrientation]);

  // Stop listening
  const stopListening = useCallback(() => {
    window.removeEventListener('deviceorientation', handleOrientation);
    setIsListening(false);
    console.log('[Angle Measurer] Stopped listening to device orientation');
  }, [handleOrientation]);

  // Handle capture with screenshot
  const handleCapture = useCallback(async () => {
    setIsCapturing(true);
    const angleString = `${currentAngle.toFixed(2)}°`;
    console.log('[Angle Measurer] Capturing angle:', angleString);
    
    try {
      // Capture screenshot of the measurement display
      if (screenshotRef.current) {
        console.log('[Angle Measurer] Taking screenshot...');
        const canvas = await html2canvas(screenshotRef.current, {
          backgroundColor: '#000000',
          scale: 2, // Higher quality
          logging: false,
          useCORS: true
        });
        
        const screenshotDataUrl = canvas.toDataURL('image/png');
        console.log('[Angle Measurer] Screenshot captured, size:', screenshotDataUrl.length);
        
        onAngleCaptured(angleString, screenshotDataUrl);
      } else {
        // Fallback if ref not available
        onAngleCaptured(angleString);
      }
    } catch (error) {
      console.error('[Angle Measurer] Screenshot failed:', error);
      // Still capture angle even if screenshot fails
      onAngleCaptured(angleString);
    } finally {
      setIsCapturing(false);
      stopListening();
      onClose();
    }
  }, [currentAngle, onAngleCaptured, stopListening, onClose]);

  // Start on mount
  useEffect(() => {
    requestPermissionAndStart();

    return () => {
      stopListening();
    };
  }, [requestPermissionAndStart, stopListening]);

  const displayAngle = currentAngle; // Keep as number for calculations
  const displayAngleText = currentAngle.toFixed(2); // Formatted text for display

  // Visual indicator: color based on typical roof pitch ranges
  const getAngleColor = (angle: number) => {
    if (angle < 10) return 'text-gray-400';
    if (angle < 20) return 'text-blue-500';
    if (angle < 35) return 'text-green-500';
    if (angle < 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getAngleLabel = (angle: number) => {
    if (angle < 10) return 'Very Low';
    if (angle < 20) return 'Low Pitch';
    if (angle < 35) return 'Standard';
    if (angle < 50) return 'Steep';
    return 'Very Steep';
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col overflow-hidden" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}>
      {/* Compact Header with iOS Safe Area */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg flex-shrink-0 px-4 app-header-padding">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold">Roof Pitch Measurer</h2>
          <button
            onClick={() => {
              stopListening();
              onClose();
            }}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Content - No Scroll - Screenshot Target */}
      <div ref={screenshotRef} className="flex-1 flex flex-col items-center justify-center px-6 py-4 overflow-hidden">
        {error ? (
          <div className="text-center">
            <div className="text-red-500 text-lg mb-4">{error}</div>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Angle Display - Compact */}
            <div className="text-center mb-6">
              <div className={`text-8xl font-bold ${getAngleColor(displayAngle)} transition-colors duration-300`}>
                {displayAngleText}°
              </div>
              <div className="text-xl text-gray-400 mt-2">
                {getAngleLabel(displayAngle)}
              </div>
            </div>

            {/* Level Indicator - Compact */}
            <div className="w-full max-w-sm mb-6">
              <div className="relative h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300"
                  style={{ width: `${(displayAngle / 90) * 100}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0°</span>
                <span>45°</span>
                <span>90°</span>
              </div>
            </div>

            {/* Instructions - Compact */}
            <div className="bg-gray-900 rounded-lg p-4 max-w-sm border border-gray-800 mb-4">
              <h3 className="text-white font-semibold mb-2 text-sm">How to Measure</h3>
              <ol className="text-gray-400 text-xs space-y-1">
                <li>1. Place phone flat on roof surface</li>
                <li>2. Align with the slope</li>
                <li>3. Wait for stabilization</li>
                <li>4. Tap button below to save</li>
              </ol>
            </div>

            {/* Status Indicator - Compact */}
            {isListening && (
              <div className="inline-flex items-center space-x-2 bg-green-900/30 text-green-400 px-3 py-1.5 rounded-full border border-green-700">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-xs font-medium">Measuring...</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Capture Button - Fixed at Bottom with iOS Safe Area */}
      {!error && (
        <div className="bg-gray-900 border-t border-gray-800 flex-shrink-0" style={{ paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px', paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
          <button
            onClick={handleCapture}
            disabled={!isListening || isCapturing}
            className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3.5 rounded-xl font-bold text-base shadow-lg hover:from-green-700 hover:to-green-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Check className="w-5 h-5" />
            <span>{isCapturing ? 'Capturing...' : `Use This Angle (${displayAngleText}°)`}</span>
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};
